// Verortung: die Rechenkerne der Standortansicht (Issue #491/#495).
//
// Hier steht alles, was ohne Karte funktioniert — Umrechnungen, Ersatzkörper und die
// unveränderlichen Änderungen am Anlagenschema. Cesium taucht in dieser Datei bewusst nicht auf:
// die Bearbeitungslogik soll ohne WebGL prüfbar sein, und der Viewer bleibt austauschbar.
//
// Zwei Festlegungen, die sich durch alles ziehen:
//
//  * **[lon, lat], Länge zuerst.** So machen es GeoJSON und CZML. Die vertauschte Reihenfolge ist
//    der häufigste Fehler beim Verorten und landet mitten im Atlantik oder in Somalia.
//  * **Kein Knoten wird stillschweigend verortet.** Wer kein `geo` trägt, steht in der Liste der
//    offenen Verortungen — sichtbar, statt an einem plausiblen, aber falschen Ort zu liegen.

import type {
  LonLat,
  PlantSchematic,
  SchematicEdge,
  SchematicGeo,
  SchematicNode,
} from '../api/types';

/** Mittlerer Erdradius (WGS84) für die Längenrechnung. */
export const ERDRADIUS_M = 6371008.8;

/** Ersatzkörper, wenn kein echter Grundriss vorliegt. */
export const KREIS_SEGMENTE = 24;
export const STANDARD_GROESSE_M = 12;
export const STANDARD_HOEHE_M = 4;

/** Großkreisabstand zweier [lon, lat]-Punkte in Metern (Haversine). */
export function streckeM(a: LonLat, b: LonLat): number {
  const bogen = (g: number) => (g * Math.PI) / 180;
  const [lon1, lat1] = [bogen(a[0]), bogen(a[1])];
  const [lon2, lat2] = [bogen(b[0]), bogen(b[1])];
  const h =
    Math.sin((lat2 - lat1) / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2;
  return 2 * ERDRADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Länge eines Leitungsverlaufs in Metern.
 *
 * Dieselbe Zahl rechnet das Backend für den CZML-Ausgang; hier steht sie, damit der Editor die
 * Länge schon beim Zeichnen anzeigt und niemand blind eine Trasse legt.
 */
export function trassenlaengeM(trasse: LonLat[]): number {
  let summe = 0;
  for (let i = 0; i + 1 < trasse.length; i += 1) summe += streckeM(trasse[i], trasse[i + 1]);
  return summe;
}

/**
 * Ring um lat/lon, wenn kein echter Grundriss hinterlegt ist.
 *
 * Die Ost-West-Ausdehnung wird mit 1/cos(Breite) gestreckt, sonst wird aus dem Kreis in unseren
 * Breiten eine liegende Ellipse.
 */
export function ersatzring(geo: SchematicGeo): LonLat[] {
  const r = (geo.groesse_m ?? STANDARD_GROESSE_M) / 2;
  const dLat = r / 110540;
  const dLon = r / (111320 * Math.max(0.05, Math.cos((geo.lat * Math.PI) / 180)));
  if (geo.koerper === 'quader') {
    return [
      [geo.lon - dLon, geo.lat - dLat],
      [geo.lon + dLon, geo.lat - dLat],
      [geo.lon + dLon, geo.lat + dLat],
      [geo.lon - dLon, geo.lat + dLat],
    ];
  }
  return Array.from({ length: KREIS_SEGMENTE }, (_, i) => {
    const w = (2 * Math.PI * i) / KREIS_SEGMENTE;
    return [geo.lon + dLon * Math.cos(w), geo.lat + dLat * Math.sin(w)] as LonLat;
  });
}

/** Der Ring, der tatsächlich gezeichnet wird: echter Grundriss, sonst Ersatzkörper. */
export function ringVon(geo: SchematicGeo): LonLat[] {
  return geo.umriss && geo.umriss.length >= 3 ? geo.umriss : ersatzring(geo);
}

/** Schwerpunkt eines Rings — die Stelle, an der eine Beschriftung sitzt. */
export function ringMittelpunkt(ring: LonLat[]): LonLat {
  const n = ring.length || 1;
  return [
    ring.reduce((s, p) => s + p[0], 0) / n,
    ring.reduce((s, p) => s + p[1], 0) / n,
  ];
}

/** Trägt der Knoten eine Welt-Position? */
export function istVerortet(node: SchematicNode): boolean {
  return typeof node.geo?.lat === 'number' && typeof node.geo?.lon === 'number';
}

/** Die noch offenen Verortungen — die Arbeitsliste des Editors. */
export function offeneVerortungen(schematic: PlantSchematic): SchematicNode[] {
  return schematic.nodes.filter((n) => !istVerortet(n));
}

/** Kann die Karte überhaupt öffnen? Ohne Standort und Kulisse gibt es nichts zu zeigen. */
export function karteMoeglich(schematic: PlantSchematic | null | undefined): boolean {
  const site = schematic?.site;
  if (!site || typeof site.mittelpunkt?.lat !== 'number') return false;
  return !!site.kulisse && site.kulisse.art !== 'keine';
}

/**
 * Mittelpunkt für den Kameraflug.
 *
 * Der gepflegte `site.mittelpunkt` gewinnt. Fehlt er, wird aus den bereits verorteten Knoten
 * gemittelt — das hilft beim ersten Verorten, wenn der Standort noch nicht gesetzt ist.
 */
export function blickpunkt(schematic: PlantSchematic): { lat: number; lon: number } | null {
  if (typeof schematic.site?.mittelpunkt?.lat === 'number') return schematic.site.mittelpunkt;
  const verortet = schematic.nodes.filter(istVerortet);
  if (verortet.length === 0) return null;
  return {
    lat: verortet.reduce((s, n) => s + (n.geo as SchematicGeo).lat, 0) / verortet.length,
    lon: verortet.reduce((s, n) => s + (n.geo as SchematicGeo).lon, 0) / verortet.length,
  };
}

// ── Änderungen am Schema: immer eine neue Kopie, nie in-place ─────────────────────────
// Der Editor arbeitet auf React-State und speichert am Ende dasselbe Objekt über
// PUT /schematic. Wer hier in-place mutiert, verliert das Rückgängigmachen und die Erkennung
// ungespeicherter Änderungen.

function mitKnoten(
  schematic: PlantSchematic,
  nodeId: string,
  aendern: (n: SchematicNode) => SchematicNode,
): PlantSchematic {
  return {
    ...schematic,
    nodes: schematic.nodes.map((n) => (n.id === nodeId ? aendern(n) : n)),
  };
}

/** Setzt oder ersetzt die Welt-Position eines Knotens. */
export function setzeGeo(
  schematic: PlantSchematic,
  nodeId: string,
  geo: SchematicGeo,
): PlantSchematic {
  return mitKnoten(schematic, nodeId, (n) => ({ ...n, geo: { ...geo } }));
}

/** Nimmt die Verortung zurück — der Knoten landet wieder in der Arbeitsliste. */
export function loeseGeo(schematic: PlantSchematic, nodeId: string): PlantSchematic {
  return mitKnoten(schematic, nodeId, ({ geo: _weg, ...rest }) => rest);
}

/**
 * Verschiebt einen verorteten Knoten — samt seines Grundrisses.
 *
 * Ein von Hand gezeichneter Umriss darf beim Ziehen nicht stehen bleiben, sonst driftet der
 * Körper von seiner Marke weg. Der Ring wandert deshalb um dieselbe Differenz mit.
 */
export function verschiebeGeo(
  schematic: PlantSchematic,
  nodeId: string,
  lat: number,
  lon: number,
): PlantSchematic {
  return mitKnoten(schematic, nodeId, (n) => {
    if (!n.geo) return { ...n, geo: { lat, lon } };
    const dLat = lat - n.geo.lat;
    const dLon = lon - n.geo.lon;
    const umriss = n.geo.umriss?.map(([x, y]) => [x + dLon, y + dLat] as LonLat);
    return { ...n, geo: { ...n.geo, lat, lon, ...(umriss ? { umriss } : {}) } };
  });
}

/**
 * Legt einen gezeichneten Grundriss an einen Knoten.
 *
 * Die Marke rückt auf den Schwerpunkt des Rings — sonst zeigen Marke und Körper auf zwei
 * verschiedene Orte, und das Verschieben würde den Ring aus dem Bild ziehen.
 * Ein Ring mit weniger als drei Punkten ist keine Fläche und wird verworfen.
 */
export function setzeUmriss(
  schematic: PlantSchematic,
  nodeId: string,
  ring: LonLat[],
): PlantSchematic {
  if (ring.length < 3) return schematic;
  const [lon, lat] = ringMittelpunkt(ring);
  return mitKnoten(schematic, nodeId, (n) => ({
    ...n,
    geo: { ...(n.geo ?? {}), lat, lon, umriss: ring.map((p) => [...p] as LonLat) },
  }));
}

/** Nimmt den Grundriss zurück; der Knoten bleibt verortet und fällt auf den Ersatzkörper. */
export function loescheUmriss(schematic: PlantSchematic, nodeId: string): PlantSchematic {
  return mitKnoten(schematic, nodeId, (n) => {
    if (!n.geo) return n;
    const { umriss: _weg, ...geo } = n.geo;
    return { ...n, geo };
  });
}

function kantenId(edge: SchematicEdge, index: number): string {
  return edge.id ?? `kante-${index}`;
}

/**
 * Legt den realen Verlauf an eine Kante.
 *
 * `illustrativ` ist kein Beiwerk: ein nachgezeichneter Verlauf darf nie als das reale Netz eines
 * Betreibers erscheinen. Der Editor setzt die Marke standardmäßig, und die Ansicht zeigt sie an.
 */
export function setzeTrasse(
  schematic: PlantSchematic,
  edgeId: string,
  trasse: LonLat[],
  illustrativ = true,
): PlantSchematic {
  if (trasse.length < 2) return schematic;
  return {
    ...schematic,
    edges: schematic.edges.map((e, i) =>
      kantenId(e, i) === edgeId
        ? { ...e, geo: { trasse: trasse.map((p) => [...p] as LonLat), illustrativ } }
        : e,
    ),
  };
}

/** Nimmt den Verlauf zurück. */
export function loescheTrasse(schematic: PlantSchematic, edgeId: string): PlantSchematic {
  return {
    ...schematic,
    edges: schematic.edges.map((e, i) => {
      if (kantenId(e, i) !== edgeId) return e;
      const { geo: _weg, ...rest } = e;
      return rest;
    }),
  };
}

/** Setzt Bauhöhe, Körperart und Größe des Ersatzkörpers. */
export function setzeKoerper(
  schematic: PlantSchematic,
  nodeId: string,
  aenderung: Partial<Pick<SchematicGeo, 'hoehe_m' | 'koerper' | 'groesse_m'>>,
): PlantSchematic {
  return mitKnoten(schematic, nodeId, (n) =>
    n.geo ? { ...n, geo: { ...n.geo, ...aenderung } } : n,
  );
}

/** Anteil der verorteten Knoten — der Fortschritt, den der Editor anzeigt. */
export function verortungsStand(schematic: PlantSchematic): { verortet: number; gesamt: number } {
  return {
    verortet: schematic.nodes.filter(istVerortet).length,
    gesamt: schematic.nodes.length,
  };
}
